import constants from "./constants";
import { dayMapping, dayOrder, facilitiesConfig } from "../config/usernameconfig";
import { formatCityName, getCityData } from "../config/cityData";
import { deriveVenueSports, sortSports } from "./sport";
import { getVisiblePitchesInCanonicalOrder } from "./turfVisibility";
/**
 * Transforms raw database turf data into SingleTileContainer format
 * This function is specifically designed for single turf view with detailed pricing
 * @param data - Raw turf data from GraphQL API
 * @param setMatchedUsernamePrice - Callback to set the matched username price
 * @returns Transformed turf data for single tile view
 */
export const transformSingleTurfData = (
  data: any,
  setMatchedUsernamePrice?: (price: number) => void,
  cityNameOverride?: string
) => {
  const visiblePitches = getVisiblePitchesInCanonicalOrder<any>(data?.Pitch);
  const resolvedCityName =
    cityNameOverride ||
    getCityData(data.city)?.name ||
    formatCityName(data.city || "");

  const convertTo12HourFormat = (time: string) => {
    try {
      // Handle ISO format: "1970-01-01T01:00:00.000Z"
      if (time && time.includes('T')) {
        const timePart = time.split('T')[1].split('.')[0]; // Extract "01:00:00"
        const [hour, minute] = timePart.split(':').map(Number);
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
      }
      
      // Handle regular HH:MM format
      if (time && time.includes(':')) {
        const [hour, minute] = time.split(":").map(Number);
        const period = hour >= 12 ? "PM" : "AM";
        const formattedHour = hour % 12 || 12;
        return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
      }
      
      // Fallback
      return "12:00 AM";
    } catch (error) {
      console.error('Error converting time format:', error, 'Time:', time);
      return "12:00 AM";
    }
  };

  const areaPriceData = visiblePitches.length
    ? visiblePitches.map((pitch: any) => ({
        area: pitch.area?.toString() || "",
        playingFormat: pitch.playingFormat || null,
        pitchName: pitch.pitchName,
        sport: pitch.sport || "CRICKET",
        supportedSports: pitch.supportedSports || [pitch.sport || "CRICKET"],
        basePrice: pitch.basePrice || 0,
        pitchDimensions: {
          length: pitch.length || 0,
          width: pitch.width || 0,
          height: pitch.height || 0,
        },
        price: pitch.dailyPricings
          ? pitch.dailyPricings
              .map((pricing: any) => {
                // Check if it's 24/7 (00:00 to 23:59)
                const is24x7 = pricing.startTime?.includes('T00:00:00') && pricing.endTime?.includes('T23:59:00');
                
                return {
                  day: dayMapping[pricing.dayOfTheWeek] || pricing.dayOfTheWeek,
                  price: pricing.price || 0,
                  startTime: is24x7 ? "All Day" : convertTo12HourFormat(pricing.startTime),
                  endTime: is24x7 ? "" : convertTo12HourFormat(pricing.endTime),
                  is24x7: is24x7
                };
              })
              .sort((a: any, b: any) => dayOrder[a.day] - dayOrder[b.day])
          : [],
      }))
    : [];

  const mondayPrice = areaPriceData[0]?.price.find((p: any) => p.day === "Monday")?.price || 0;
  if (setMatchedUsernamePrice) {
    setMatchedUsernamePrice(mondayPrice);
  }

  // Build imageUrl from sportImages - all images from all sports
  let imageUrl: string[] = [];
  if (data.sportImages) {
    try {
      const sportImagesObj: Record<string, string[]> =
        typeof data.sportImages === 'string'
          ? JSON.parse(data.sportImages)
          : data.sportImages;
      imageUrl = Object.values(sportImagesObj).flat();
    } catch {
      imageUrl = [];
    }
  }
  if (imageUrl.length === 0) {
    imageUrl = Array.isArray(data.images) ? data.images : [data.images || ""];
  }

  return {
    name: data.name || "",
    domainKey: data.name || "",
    imageUrl,
    logo: data.logo || "",
    fields: [
      { label: "Contact", key: "actualContact", type: "link" },
      { label: "Contact", key: "displayContact", type: "link" },
      { label: "Partnership", key: "partnership", type: "boolean" },
      { label: "Area & Price", key: "areaPrice", type: "object" },
      { label: "Location", key: "location", type: "float" },
      { label: "Availability Time", key: "availabilityData", type: "object" },
      { label: "Rain Protected", key: "isRainProtected", type: "string" },
      { label: "Cafe", key: "hasCafe", type: "boolean" },
      { label: "Address", key: "address", type: "string" },
    ],
    data: {
      city: resolvedCityName,
      areaName: data.areaName || "",
      areaId: data.areaId || "",
      areaPrice: areaPriceData,
      address: data.address || "",
      structuredAddress: {
        streetName: data.streetName || "",
        landmark: data.landmark || "",
        state: data.state || "",
        pincode: data.pincode || "",
      },
      location: {
        latitude: data.latitude || "",
        longitude: data.longitude || "",
      },
      actualContact: data.supportNumber || "",
      displayContact: constants.BUISNESS_NUMBER,
      partnership: data.isPartner || false,
      availabilityData: {
        openingTime: data.openingTime || "",
        closingTime: data.closingTime || "",
        isAvailable24Hours: data.isAvailable24Hours || false,
      },
      isRainProtected: data.isCovered || false,
      starRating: data.googleRating?.toString() || "",
      hasCafe: data.hasCafe || false,
      supportedSports: deriveVenueSports({ pitches: visiblePitches }),
      enabledSports: sortSports(data.supportedSports || []),
      sportImages: data.sportImages || null,
      workingHours: data.workingHours || [],
      facilities: data.facilities?.length
        ? data.facilities
        : [
            "Great ambience",
            "Bat-Ball Provided",
            ...Object.entries(facilitiesConfig)
              .filter(([key]) => data[key])
              .map(([, value]) => value),
          ],
    },
    userName: data.username || "",
  };
}; 